import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import departmentService from '../../services/departmentService';
import userService from '../../services/userService';
import './DepartmentForm.css';

const DepartmentForm = ({ departmentId, initialData }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    parent_department_id: '',
    manager_id: '',
    budget_allocated: 0,
    budget_spent: 0,
    is_active: true
  });

  useEffect(() => {
    fetchDepartments();
    fetchUsers();
    
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        code: initialData.code || '',
        description: initialData.description || '',
        parent_department_id: initialData.parent_department_id || '',
        manager_id: initialData.manager_id || '',
        budget_allocated: initialData.budget_allocated || 0,
        budget_spent: initialData.budget_spent || 0,
        is_active: initialData.is_active !== undefined ? initialData.is_active : true
      });
    }
  }, [initialData]);

  const fetchDepartments = async () => {
    try {
      const data = await departmentService.getAllDepartments();
      // Filter out current department if editing (prevent self-parent)
      const filtered = departmentId 
        ? data.departments.filter(d => d.id !== departmentId)
        : data.departments;
      setDepartments(filtered);
    } catch (err) {
      console.error('Failed to fetch departments:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await userService.getAll({ is_active: true });
      setUsers(data.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validate budget
      if (parseFloat(formData.budget_spent) > parseFloat(formData.budget_allocated)) {
        setError('Budget spent cannot exceed budget allocated');
        setLoading(false);
        return;
      }

      const submitData = {
        ...formData,
        parent_department_id: formData.parent_department_id || null,
        manager_id: formData.manager_id || null,
        budget_allocated: parseFloat(formData.budget_allocated) || 0,
        budget_spent: parseFloat(formData.budget_spent) || 0
      };

      if (departmentId) {
        await departmentService.updateDepartment(departmentId, submitData);
      } else {
        await departmentService.createDepartment(submitData);
      }

      navigate('/app/departments');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save department');
    } finally {
      setLoading(false);
    }
  };

  const budgetUtilization = formData.budget_allocated > 0
    ? ((formData.budget_spent / formData.budget_allocated) * 100).toFixed(2)
    : 0;

  const budgetStatus = departmentService.getBudgetStatus(budgetUtilization);

  return (
    <form onSubmit={handleSubmit} className="department-form">
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="form-section">
        <h3>Basic Information</h3>
        
        <div className="form-row custom-row">
          <div className="form-group">
            <label htmlFor="name">
              Department Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              className="form-control"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="e.g., Engineering, Sales, Marketing"
            />
          </div>

          <div className="form-group">
            <label htmlFor="code">Department Code</label>
            <input
              type="text"
              id="code"
              name="code"
              className="form-control"
              value={formData.code}
              onChange={handleChange}
              placeholder="e.g., ENG, SALES, MKT"
            />
            <small className="form-text">Unique identifier for the department</small>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            className="form-control"
            value={formData.description}
            onChange={handleChange}
            rows="3"
            placeholder="Brief description of the department's role and responsibilities"
          />
        </div>
      </div>

      <div className="form-section">
        <h3>Organizational Structure</h3>
        
        <div className="form-row custom-row">
          <div className="form-group">
            <label htmlFor="parent_department_id">Parent Department</label>
            <select
              id="parent_department_id"
              name="parent_department_id"
              className="form-control"
              value={formData.parent_department_id}
              onChange={handleChange}
            >
              <option value="">None (Root Department)</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>
                  {dept.name} {dept.code ? `(${dept.code})` : ''}
                </option>
              ))}
            </select>
            <small className="form-text">Select a parent department to create a sub-department</small>
          </div>

          <div className="form-group">
            <label htmlFor="manager_id">Department Manager</label>
            <select
              id="manager_id"
              name="manager_id"
              className="form-control"
              value={formData.manager_id}
              onChange={handleChange}
            >
              <option value="">No Manager Assigned</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.first_name} {user.last_name} ({user.email})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Budget Management</h3>
        
        <div className="form-row custom-row" >
          <div className="form-group">
            <label htmlFor="budget_allocated">Budget Allocated ($)</label>
            <input
              type="number"
              id="budget_allocated"
              name="budget_allocated"
              className="form-control"
              value={formData.budget_allocated}
              onChange={handleChange}
              min="0"
              step="0.01"
            />
          </div>

          <div className="form-group">
            <label htmlFor="budget_spent">Budget Spent ($)</label>
            <input
              type="number"
              id="budget_spent"
              name="budget_spent"
              className="form-control"
              value={formData.budget_spent}
              onChange={handleChange}
              min="0"
              step="0.01"
            />
          </div>
        </div>

        {formData.budget_allocated > 0 && (
          <div className="budget-preview">
            <div className="budget-header">
              <span>Budget Utilization</span>
              <span className={`budget-percentage budget-${budgetStatus}`}>
                {budgetUtilization}%
              </span>
            </div>
            <div className="budget-bar">
              <div 
                className={`budget-fill budget-${budgetStatus}`}
                style={{ width: `${Math.min(budgetUtilization, 100)}%` }}
              />
            </div>
            <div className="budget-summary">
              <div>
                <strong>Allocated:</strong> {departmentService.formatBudget(formData.budget_allocated)}
              </div>
              <div>
                <strong>Spent:</strong> {departmentService.formatBudget(formData.budget_spent)}
              </div>
              <div>
                <strong>Remaining:</strong> {departmentService.formatBudget(
                  formData.budget_allocated - formData.budget_spent
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="form-section">
        <h3>Status</h3>
        
        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
            />
            <span>Active Department</span>
          </label>
          <small className="form-text">
            Inactive departments are hidden from most views but can be reactivated
          </small>
        </div>
      </div>

      <div className="form-actions">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => navigate('/app/departments')}
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={loading}
        >
          {loading ? 'Saving...' : departmentId ? 'Update Department' : 'Create Department'}
        </button>
      </div>
    </form>
  );
};

export default DepartmentForm;

